// src/app/views/landing/Landing.jsx
import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getPathByCdExt } from "app/utils/utils";
import { EXAM_TYPES } from "app/types/examTypes";

// Styled components with improved responsiveness
const StyledBanner = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '180px',
  height: 'auto',
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  overflow: 'hidden',
  padding: theme.spacing(2, 2),
  [theme.breakpoints.down('sm')]: {
    minHeight: '180px',
  },
}));

const OpenBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  background: theme.palette.warning.main,
  color: theme.palette.primary.dark,
  padding: theme.spacing(1, 2),
  transform: 'skew(-12deg)',
  '& > *': {
    transform: 'skew(12deg)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  fontSize: '1rem',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  transition: 'all 0.3s ease',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': {
    backgroundColor: theme.palette.grey[300],
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(0.8),
  },
}));

const GroupContainer = styled(Paper)(({ theme, colorgroup }) => ({
  padding: theme.spacing(1.5, 1.5),
  marginBottom: theme.spacing(1.5),
  borderWidth: 2,
  borderStyle: 'solid',
  minHeight: 'auto',
  display: 'flex',
  flexDirection: 'column',
  ...(colorgroup === '5급' && {
    backgroundColor: theme.palette.primary.light,
    borderColor: theme.palette.primary.main,
  }),
  ...(colorgroup === 'LEET' && {
    backgroundColor: theme.palette.success.light,
    borderColor: theme.palette.success.main,
  }),
  ...(colorgroup === '7급' && {
    backgroundColor: theme.palette.secondary.light,
    borderColor: theme.palette.secondary.main,
  }),
  ...(colorgroup === '7급2차' && {
    backgroundColor: theme.palette.warning.light,
    borderColor: theme.palette.warning.main,
  }),
  ...(colorgroup === '변호사 시험' && {
    backgroundColor: theme.palette.info.light,
    borderColor: theme.palette.info.main,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const ResponsiveTypography = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '90%',
  },
}));

export default function Landing() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      {/* Banner */}
      <StyledBanner>
        {/* <OpenBadge>
          <Typography variant="subtitle2" fontWeight="bold">OPEN</Typography>
          <Typography variant="caption">24.10.02.</Typography>
          <Typography variant="caption">~</Typography>
        </OpenBadge> */}
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center'
        }}>
          <ResponsiveTypography variant="h6" mb={1}>오리지널의 힘, 법률저널 전국모의고사</ResponsiveTypography>
          <ResponsiveTypography variant="subtitle1" mb={1}>가장 실전답게, 차원이 다른 전략</ResponsiveTypography>
          <ResponsiveTypography variant="h3" fontWeight="bold" mb={1}>법률저널 시험 센터</ResponsiveTypography>
          <ResponsiveTypography variant="body2" mt={1}>
            | LEET · 5급공채 · 외교관후보자 · 입법고시 · 법원행시 · 지역인재 7급 · 7급 공채 · 7급 2차 · 변호사 시험 |
          </ResponsiveTypography>
        </Box>
      </StyledBanner>

      {/* Main Content */}
      <Grid container spacing={2}>
        {/* 5급 Group */}
        <Grid item xs={12} sm={6}>
          <GroupContainer colorgroup="5급">
            <ResponsiveTypography variant="h6" fontWeight="bold" mb={1}>5급</ResponsiveTypography>
            <Box sx={{ flexGrow: 1 }}>
              <StyledButton variant="contained" onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G5.rid_cd_ext)}/land/${EXAM_TYPES.G5.rid_cd_ext}/${EXAM_TYPES.G5.rid_imp_sr}`)}>
                5급 PSAT
              </StyledButton>
              {process.env.NODE_ENV === '-' ? (
                <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}>
                  5급 예측시스템
                </StyledButton>
              ) : (
                <StyledButton variant="contained" onClick={() => {
                  navigate(`/pre/${EXAM_TYPES.G5.rid_cd_ext}/verify?ext=${EXAM_TYPES.G5.rid_cd_ext}&yr=${currentYear}`);
                }}>
                  5급 예측시스템
                </StyledButton>
              )}
            </Box>
          </GroupContainer>
        </Grid>

        {/* LEET Group */}
        <Grid item xs={12} sm={6}>
          <GroupContainer colorgroup="LEET">
            <ResponsiveTypography variant="h6" fontWeight="bold" mb={1}>LEET</ResponsiveTypography>
            <Box sx={{ flexGrow: 1 }}>
              <StyledButton variant="contained" onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.LEET.rid_cd_ext)}/land/${EXAM_TYPES.LEET.rid_cd_ext}/${EXAM_TYPES.LEET.rid_imp_sr}`)}>
                LEET
              </StyledButton>
              {process.env.NODE_ENV === '-' ? (
                <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}>
                  LEET 예측시스템
                </StyledButton>
              ) : (
                <StyledButton variant="contained" onClick={() => {
                  navigate(`/pre/${EXAM_TYPES.LEET.rid_cd_ext}/verify?ext=${EXAM_TYPES.LEET.rid_cd_ext}&yr=${currentYear}`);
                }}>
                  LEET 예측시스템
                </StyledButton>
              )}
            </Box>
          </GroupContainer>
        </Grid>

        {/* 7급 Group */}
        <Grid item xs={12} sm={6}>
          <GroupContainer colorgroup="7급">
            <ResponsiveTypography variant="h6" fontWeight="bold" mb={1}>7급</ResponsiveTypography>
            <Box sx={{ flexGrow: 1 }}>
              {/* <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}> */}
              <StyledButton variant="contained" onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G7.rid_cd_ext)}/land/${EXAM_TYPES.G7.rid_cd_ext}/${EXAM_TYPES.G7.rid_imp_sr}`)}>
                7급 PSAT
              </StyledButton>
              {process.env.NODE_ENV === '-' ? (
                <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}>
                  7급 예측시스템
                </StyledButton>
              ) : (
                <StyledButton variant="contained" onClick={() => {
                  navigate(`/pre/${EXAM_TYPES.G7.rid_cd_ext}/verify?ext=${EXAM_TYPES.G7.rid_cd_ext}&yr=${currentYear}`);
                }}>
                  7급 예측시스템
                </StyledButton>
              )}
            </Box>
          </GroupContainer>
        </Grid>

        {/* 7급 2차 Group */}
        <Grid item xs={12} sm={6}>
          <GroupContainer colorgroup="7급2차">
            <ResponsiveTypography variant="h6" fontWeight="bold" mb={1}>7급 2차</ResponsiveTypography>
            <Box sx={{ flexGrow: 1 }}>
              {process.env.NODE_ENV === '-' ? (
                <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}>
                  7급 2차
                </StyledButton>
              ) : (
                <StyledButton variant="contained" onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G72.rid_cd_ext)}/land/${EXAM_TYPES.G72.rid_cd_ext}/${EXAM_TYPES.G72.rid_imp_sr}`)}>
                  7급 2차
                </StyledButton>
              )}
            </Box>
          </GroupContainer>
        </Grid>

        {/* 변호사 시험 Group */}
        <Grid item xs={12} sm={6}>
          <GroupContainer colorgroup="변호사 시험">
            <ResponsiveTypography variant="h6" fontWeight="bold" mb={1}>변호사 시험</ResponsiveTypography>
            <Box sx={{ flexGrow: 1 }}>
              {process.env.NODE_ENV === '-' ? (
                <StyledButton variant="contained" onClick={() => { alert('준비 중입니다.'); }}>
                  변호사 시험
                </StyledButton>
              ) : (
                <StyledButton variant="contained" onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.BAR.rid_cd_ext)}/land/${EXAM_TYPES.BAR.rid_cd_ext}/${EXAM_TYPES.BAR.rid_imp_sr}`)}>
                  변호사 시험
                </StyledButton>
              )}
            </Box>
          </GroupContainer>
        </Grid>
      </Grid>

      {/* External Links */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mt: 2
          }}
        >
          <Button
            variant="outlined"
            size="large"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              padding: { xs: '8px 16px', sm: '10px 24px' },
              minWidth: { xs: '200px', sm: '160px' }
            }}
            onClick={() => window.open('https://cafe.naver.com/lecpsat', '_blank')}
          >
            PSAT 카페
          </Button>

          <Button
            variant="outlined"
            size="large"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              padding: { xs: '8px 16px', sm: '10px 24px' },
              minWidth: { xs: '200px', sm: '160px' }
            }}
            onClick={() => window.open('https://cafe.naver.com/lecleet', '_blank')}
          >
            LEET 카페
          </Button>
          <Button
            variant="outlined"
            size="large"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              padding: { xs: '8px 16px', sm: '10px 24px' },
              minWidth: { xs: '200px', sm: '160px' }
            }}
            onClick={() => window.open('http://www.lec.co.kr', '_blank')}
          >
            법률저널 홈페이지
          </Button>
        </Box>
      </Box>
    </Container>
  );
}